<script setup lang="ts">
import type { UserSummary } from '@beeboard/common';

/**
 * Props interface for user mention dropdown component.
 * These prop names are required by TipTap's mention extension contract.
 * - 'items': Must be named exactly 'items' to match SuggestionProps interface
 * - 'command': Callback function provided by TipTap to insert selected mention into editor
 */
interface Props {
  items: UserSummary[];
  command: (props: { id: string; label: string }) => void;
}

const props = defineProps<Props>();

const users = toRef(props, 'items');

const data = reactive({
  selectedIndex: 0,
});

watch(users, () => {
  data.selectedIndex = 0;
});

const getUserDisplayName = (user: UserSummary) => {
  const firstName = user.first_name || '';
  const lastName = user.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || user.username || 'Unknown User';
};

const upHandler = () => {
  if (users.value.length === 0) {
    return;
  }
  data.selectedIndex =
    (data.selectedIndex + users.value.length - 1) % users.value.length;
};

const downHandler = () => {
  if (users.value.length === 0) {
    return;
  }
  data.selectedIndex = (data.selectedIndex + 1) % users.value.length;
};

const enterHandler = () => {
  if (users.value.length === 0) {
    return;
  }
  selectUser(data.selectedIndex);
};

const selectUser = (index: number) => {
  const user = users.value[index];
  if (user) {
    props.command({
      id: user.id,
      label: getUserDisplayName(user),
    });
  }
};

defineExpose({
  onKeyDown: ({ event }: { event: KeyboardEvent }) => {
    if (event.key === 'ArrowUp') {
      upHandler();

      return true;
    }

    if (event.key === 'ArrowDown') {
      downHandler();

      return true;
    }

    if (event.key === 'Enter') {
      enterHandler();

      return true;
    }

    return false;
  },
});
</script>

<template>
  <div class="mention-list">
    <div v-if="users.length === 0" class="mention-empty">
      {{ $t('comments.noUsersFound') }}
    </div>
    <div
      v-for="(user, index) in users"
      v-else
      :key="user.id"
      class="mention-item"
      :class="{ 'is-selected': index === data.selectedIndex }"
      @mousedown.prevent="selectUser(index)"
    >
      <div class="user-avatar">
        {{ getUserDisplayName(user).charAt(0).toUpperCase() }}
      </div>
      <div class="user-info">
        <div class="user-name">{{ getUserDisplayName(user) }}</div>
        <div v-if="user.email" class="user-email">{{ user.email }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.mention-list {
  background: white;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-6);
  box-shadow: var(--shadow-lg);
  max-height: 200px;
  overflow-y: auto;
  padding: var(--space-4);
  z-index: var(--z-popover);

  .mention-empty {
    padding: var(--space-12);
    text-align: center;
    color: var(--color-gray-500);
    font-size: var(--font-size-14);
    font-style: italic;
  }

  .mention-item {
    display: flex;
    align-items: center;
    padding: var(--space-8) var(--space-12);
    border-radius: var(--border-radius-4);
    cursor: pointer;
    transition: background-color var(--duration-base);

    &:hover,
    &.is-selected {
      background-color: var(--color-primary-50, #f0f9ff);
    }

    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: var(--border-radius-circle);
      background-color: var(--color-primary-500, #3b82f6);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-14);
      margin-right: var(--space-12);
      flex-shrink: 0;
    }

    .user-info {
      flex: 1;
      min-width: 0;

      .user-name {
        font-weight: var(--font-weight-medium);
        color: var(--color-gray-900);
        font-size: var(--font-size-14);
        line-height: var(--line-height-ratio-120);
      }

      .user-email {
        color: var(--color-gray-500);
        font-size: var(--font-size-12);
        line-height: var(--line-height-ratio-120);
        margin-top: var(--space-2);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}
</style>
